import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatScore, ordinal, daysLeftInMonth, clockTime } from './scorekeeping/format';
import { isFromCache, fetchWithStatus } from '../services/connectionStatus';
import { useOnReconnect } from '../hooks/useOnReconnect';
import { useTvStage } from '../hooks/useTvStage';
import { readTvSnapshot, writeTvSnapshot } from '../utils/tvSnapshot';
import './ScorekeepingTv.css';

/**
 * The club screen.
 *
 * Runs unattended on a TV through signage software, at 1920x1080, with nobody
 * touching it and no account to sign in with. It replaces two boards that used
 * to be shown separately: the monthly competition and the per-machine tables.
 *
 * The layout is fixed at 1920x1080 and scaled to whatever it is actually shown
 * on, so the proportions the design was drawn at survive a panel that is not
 * exactly that size. Signage hardware lies about its resolution often enough
 * that laying this out responsively would mean debugging a TV across the room.
 *
 * Three things are load-bearing and easy to break by accident:
 *
 *   - **The rotation is never interrupted.** A score arriving does not change the
 *     page. Somebody scanning page 6 of 10 for their machine must not have it
 *     snatched away, and an interruption is the one behaviour that would make the
 *     whole rotation untrustworthy.
 *   - **The ticker is the point.** It is why this screen is worth building rather
 *     than keeping the two it replaces. A player who has just finished sees their
 *     name within seconds instead of waiting out a hundred-second cycle.
 *   - **Losing the connection changes almost nothing.** The rotation and the
 *     ticker carry on with the last data received. Being offline is stated twice,
 *     quietly, rather than taking the screen over.
 */

// Below this the standings column and four machine cards stop fitting sensibly,
// so a very wide-but-short window letterboxes vertically instead of squeezing.
const MIN_STAGE_WIDTH = 1600;
const MACHINES_PER_PAGE = 4;
const ROWS_PER_MACHINE = 10;

// How long without news before the screen admits it is offline. Generous on
// purpose: a brief blip is not worth telling the room about.
const OFFLINE_AFTER_MS = 90000;
// Fallback when the live connection is unavailable, which may be permanent if the
// signage browser cannot hold a socket. The screen must work either way.
const POLL_MS = 30000;
const TICKER_LENGTH = 12;
const SNAPSHOT_KEY = 'scorekeeping-display';

/**
 * Where the live channel lives.
 *
 * In production the app and the API are served from one origin, so a same-origin
 * socket is correct. In development they are not: the React dev server sits in
 * front on another port and uses `/ws` for its own hot-reload socket, so a
 * same-origin connection is answered by the dev server rather than the API and
 * closes with an origin complaint.
 *
 * Nothing breaks when this is wrong, because the screen falls back to polling.
 * It is worth getting right anyway so the fallback is exercised only when it is
 * genuinely needed rather than every time somebody works on this page.
 */
function liveSocketUrl() {
  if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const apiPort = process.env.REACT_APP_API_PORT || '3000';
  const host = process.env.NODE_ENV === 'production'
    ? window.location.host
    : `${window.location.hostname}:${apiPort}`;

  return `${protocol}//${host}/ws?type=display`;
}

function ScorekeepingTv() {
  // Start from the last live data this browser saw, so a reload while the
  // server is down still fills the room. It is marked offline until the
  // server answers, and dated by its own generatedAt.
  const [snapshot] = useState(() => readTvSnapshot(SNAPSHOT_KEY));
  const [data, setData] = useState(() => snapshot?.data ?? null);
  const [ticker, setTicker] = useState(() => snapshot?.data?.recent || []);
  const [page, setPage] = useState(0);
  // Nothing live has arrived yet, so the screen does not claim to be live. A
  // refresh during an outage used to show "Live" for 90 seconds over saved data.
  const [lastNewsAt, setLastNewsAt] = useState(null);
  const [offline, setOffline] = useState(true);
  const [bootFailed, setBootFailed] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const socketRef = useRef(null);
  const stage = useTvStage(MIN_STAGE_WIDTH);

  const load = useCallback(async () => {
    try {
      // Reported to connectionStatus, which is what notices the server coming
      // back and triggers the reload below.
      const response = await fetchWithStatus('/api/scorekeeping/display');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      setData(payload);
      setTicker((current) => (current.length === 0 ? payload.recent || [] : current));
      // With the server down, the service worker answers with the copy it kept.
      // That is worth showing but it is not news: counting it as news reset the
      // offline timer on every poll, so the screen never admitted it was offline.
      if (!isFromCache(response)) {
        setLastNewsAt(Date.now());
        setOffline(false);
        writeTvSnapshot(SNAPSHOT_KEY, payload);
      }
    } catch (error) {
      // Deliberately quiet. The screen keeps showing what it has; the offline
      // timer below is what eventually tells the room.
      console.warn('Club screen could not refresh:', error.message);
      setBootFailed(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The server is back: reload now rather than at the next poll.
  useOnReconnect(load);

  // The live connection. If the signage browser cannot hold a socket, this simply
  // never connects and the poll below carries the screen on its own.
  useEffect(() => {
    let closed = false;
    let retry;

    const connect = () => {
      if (closed) return;
      try {
        const socket = new WebSocket(liveSocketUrl());
        socketRef.current = socket;

        socket.onopen = () => {
          setLiveConnected(true);
          setLastNewsAt(Date.now());
          setOffline(false);
          // Saying "Live" over data from before an outage would be wrong, so
          // fetch what changed while the socket was down.
          load();
        };

        socket.onmessage = (event) => {
          setLastNewsAt(Date.now());
          setOffline(false);
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'scorekeeping:score' && message.score) {
              // Straight into the ticker. The rotation is untouched on purpose.
              setTicker((current) => [message.score, ...current].slice(0, TICKER_LENGTH));
              // The standings may have moved, so pick them up without disturbing
              // what is on screen.
              load();
            }
            if (message.type === 'scorekeeping:standings') load();
          } catch {
            // A malformed frame is not worth reacting to.
          }
        };

        socket.onclose = () => {
          setLiveConnected(false);
          socketRef.current = null;
          if (!closed) retry = setTimeout(connect, 10000);
        };
        socket.onerror = () => socket.close();
      } catch (error) {
        if (!closed) retry = setTimeout(connect, 10000);
      }
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      if (socketRef.current) socketRef.current.close();
    };
  }, [load]);

  // Polling runs regardless. It is the only refresh when there is no socket, and
  // a cheap safety net when there is.
  useEffect(() => {
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  // With nothing on screen yet, keep trying sooner than the regular poll.
  useEffect(() => {
    if (data) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [data, load]);

  useEffect(() => {
    if (lastNewsAt === null) return undefined;
    const timer = setInterval(() => {
      setOffline(Date.now() - lastNewsAt > OFFLINE_AFTER_MS);
    }, 5000);
    return () => clearInterval(timer);
  }, [lastNewsAt]);

  const boards = useMemo(() => data?.boards || [], [data]);
  const pageCount = Math.max(1, Math.ceil(boards.length / MACHINES_PER_PAGE));

  useEffect(() => {
    const seconds = data?.pageRotationSeconds || 10;
    const timer = setInterval(() => setPage((p) => (p + 1) % pageCount), seconds * 1000);
    return () => clearInterval(timer);
  }, [data?.pageRotationSeconds, pageCount]);

  useEffect(() => { if (page >= pageCount) setPage(0); }, [page, pageCount]);

  if (!data) {
    return (
      <div className="sktv-root">
        <div className="sktv-boot">
          {bootFailed ? "Can't reach the club server. Trying again…" : 'Loading the club leaderboard…'}
        </div>
      </div>
    );
  }

  const standings = data.standings?.standings || [];
  const pageMachines = boards.slice(page * MACHINES_PER_PAGE, page * MACHINES_PER_PAGE + MACHINES_PER_PAGE);
  const firstIndex = page * MACHINES_PER_PAGE + 1;
  const lastIndex = Math.min(firstIndex + MACHINES_PER_PAGE - 1, boards.length);
  const tickerItems = ticker.length > 0 ? ticker : (data.recent || []);
  const asOf = clockTime(data.generatedAt);

  // Duration is per entry, not per loop.
  //
  // A fixed loop time makes the ticker speed depend on how much is in it: three
  // scores crawl and twenty sprint, which is exactly backwards, because a busy
  // night is when people most want to read it. Scaling the duration with the
  // number of entries keeps a constant pixel speed whatever the ticker holds.
  const tickerSeconds = Math.max(
    12,
    tickerItems.length * (data.tickerSecondsPerItem || 7)
  );

  return (
    <div className="sktv-root">
      <div
        className="sktv-stage"
        style={{
          width: `${stage.width}px`,
          transform: `translate(-50%, -50%) scale(${stage.scale})`
        }}
      >
        <header className={`sktv-header ${offline ? 'is-offline' : ''}`}>
          <div className="sktv-header__left">
            <span className="sktv-wordmark">CAPITAL PINBALL</span>
            <span className="sktv-month">{data.standings?.label}</span>
          </div>
          <div className="sktv-header__right">
            <span className="sktv-meta">
              {standings.length} players · {data.standings?.countingMachineCount} machines counting
            </span>
            {offline ? (
              <span className="sktv-pill sktv-pill--warning">
                <span className="sktv-dot" />Standings as of {asOf}
              </span>
            ) : (
              <span className="sktv-pill sktv-pill--live">
                <span className="sktv-dot sktv-dot--pulse" />Live
              </span>
            )}
            <span className="sktv-days">{daysLeftInMonth()} days left</span>
          </div>
        </header>

        <div className="sktv-body">
          <section className={`sktv-standings ${offline ? 'is-stale' : ''}`}>
            <div className="sktv-standings__head">
              <p className="sktv-standings__title">Club standings</p>
              <p className="sktv-standings__sub">Best {data.standings?.countedMachineLimit} machines each</p>
            </div>
            <div className="sktv-standings__list">
              {standings.length === 0 && (
                <div className="sktv-empty">
                  The first score of {data.standings?.label?.split(' ')[0]} takes first place.
                </div>
              )}
              {standings.slice(0, 12).map((player) => (
                <div className="sktv-standings__row" key={`${player.position}-${player.playerName}`}>
                  <span className="sktv-standings__rank">{ordinal(player.position)}</span>
                  {/* The person first and the handle under it, as the app shows
                      them, so a member reads the same on the wall as on their phone. */}
                  <span className="sktv-standings__who">
                    <span className="sktv-standings__name">{player.displayName || player.playerName}</span>
                    {player.displayName && (
                      <span className="sktv-standings__handle">@{player.playerName}</span>
                    )}
                  </span>
                  <span className="sktv-standings__points">{formatScore(player.totalPoints)}</span>
                </div>
              ))}
            </div>
            <div className="sktv-standings__foot">
              Top four in {data.standings?.label?.split(' ')[0]} earn a badge
            </div>
          </section>

          <section className="sktv-machines">
            <div className="sktv-machines__head">
              <p className="sktv-machines__label">
                {boards.length === 0
                  ? 'No machines yet'
                  : `Machines ${firstIndex} to ${lastIndex} of ${boards.length}`}
              </p>
              <div className="sktv-dots">
                {Array.from({ length: pageCount }, (_, i) => (
                  <span key={i} className={`sktv-dots__dot ${i === page ? 'is-on' : ''}`} />
                ))}
              </div>
            </div>
            <div className="sktv-machines__grid">
              {pageMachines.map((board) => (
                <article className="sktv-card" key={board.machineId}>
                  <div
                    className="sktv-card__head"
                    style={board.imageUrl ? { backgroundImage: `url("${board.imageUrl}")` } : undefined}
                  >
                    <span className="sktv-card__scrim" />
                    <span className="sktv-card__title">{board.machineName}</span>
                  </div>
                  <div className="sktv-card__rows">
                    {board.entries.length === 0 ? (
                      <div className="sktv-card__empty">No scores recorded yet</div>
                    ) : (
                      Array.from({ length: ROWS_PER_MACHINE }, (_, i) => board.entries[i]).map((entry, i) => (
                        <div className="sktv-card__row" key={i}>
                          {entry ? (
                            <>
                              <span className="sktv-card__rank">{entry.rank}.</span>
                              <span className="sktv-card__name">{entry.playerName}</span>
                              <span className="sktv-card__score">{formatScore(entry.score)}</span>
                            </>
                          ) : <span className="sktv-card__blank" />}
                        </div>
                      ))
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <footer className="sktv-ticker">
          <div className="sktv-ticker__label">JUST PLAYED</div>
          <div className="sktv-ticker__viewport">
            {tickerItems.length === 0 ? (
              <span className="sktv-ticker__quiet">
                Nothing played yet. Scan the code on any machine to get on the board.
              </span>
            ) : (
              <div
                className="sktv-ticker__track"
                style={{ animationDuration: `${tickerSeconds}s` }}
              >
                {[0, 1].map((copy) => (
                  <div className="sktv-ticker__run" key={copy} aria-hidden={copy === 1}>
                    {tickerItems.map((item, i) => (
                      <span className="sktv-ticker__item" key={`${copy}-${item.id}-${i}`}>
                        <span className="sktv-ticker__player">{item.playerName}</span>
                        <span className="sktv-ticker__machine">{item.machineName}</span>
                        <span className="sktv-ticker__score">{formatScore(item.score)}</span>
                        {item.rank && (
                          <span className="sktv-ticker__place">
                            {ordinal(item.rank)} on this machine
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          {offline && (
            <div className="sktv-ticker__offline">
              <span className="sktv-dot" />Offline · as of {asOf}
            </div>
          )}
          {!offline && !liveConnected && <span className="sktv-ticker__edge" />}
        </footer>
      </div>
    </div>
  );
}

export default ScorekeepingTv;
